import { observer } from 'mobx-react-lite';
import { useNavigate, useSearchParams, useParams } from 'react-router-dom';
import { Button } from '@shared/components/common/Button';
import { CheckCircle2, Loader, XCircle } from 'lucide-react';
import { ROUTES } from '@shared/configs/constants';
import { useEffect, useState } from 'react';
import Heading from '@shared/components/common/Heading';
import { useStore } from '@shared/hooks/useStore';
import { toastify } from '@shared/utils/toastify';
import { formatPrice } from '@shared/utils/utils';
import { flowResult } from 'mobx';
import { ORDER_STATUS } from '@shared/types/enums';

interface OrderDetails {
    orderId: number,
    orderCode: string,
    total: number,
    subtotal: number,
    tax: number,
    paymentMethod: string
}

enum PaymentStatus {
    SUCCESS = 'success',
    FAILED = 'failed',
    PENDING = 'pending'
}

export default observer(function PaymentResultPage() {
    const navigate = useNavigate();
    const [searchParams] = useSearchParams();
    const params = useParams<{ id?: string }>();
    const { cartStore } = useStore();

    // Get order ID from URL params (from /cart/checkout/order/:id/success) or query params
    const orderIdFromParams = params.id;
    const orderIdFromQuery = searchParams.get('OrderID') || searchParams.get('orderId') || searchParams.get('order_id');
    const orderId = orderIdFromParams || orderIdFromQuery;

    const [isLoading, setIsLoading] = useState(true);
    const [paymentStatus, setPaymentStatus] = useState<PaymentStatus>();
    const [orderDetails, setOrderDetails] = useState<OrderDetails | null>(null);

    useEffect(() => {
        // Load order details from backend to verify payment
        const loadOrderDetails = async () => {
            try {
                if (!orderId) {
                    // No orderId, redirect to cart
                    toastify('error', '注文情報が見つかりませんでした');
                    navigate(ROUTES.cart.href);
                    return;
                }

                // Get order detail from API to verify payment status
                const orderData = await flowResult(cartStore.getOrderDetail(orderId));
                // orderData.code === localStorage.getItem('order_code')
                if (orderData && orderData.code === localStorage.getItem('order_code')) {
                    // Map order data to OrderDetails format
                    setOrderDetails({
                        orderId: orderData.id,
                        orderCode: orderData.code,
                        total: orderData.totalAmount,
                        subtotal: orderData.preAmount,
                        tax: orderData.tax,
                        paymentMethod: orderData.paymentMethod
                    });
                    if ((orderData.status === ORDER_STATUS.FAILURE) || (orderData.status === ORDER_STATUS.NOT_PAID)) {
                        setPaymentStatus(PaymentStatus.FAILED);
                        return;
                    }
                    setPaymentStatus(PaymentStatus.SUCCESS);
                    // Clear cart after successful payment
                    cartStore.fetchCartItems();
                } else {
                    navigate(ROUTES.cart.href);
                }
            } catch (error: any) {
                console.error('Payment verification error:', error);
                toastify('error', error?.message || error?.response?.data?.message || '支払い確認に失敗しました');
                setPaymentStatus(PaymentStatus.FAILED);
            } finally {
                localStorage.removeItem('order_code');
                setIsLoading(false);
            }
        };

        loadOrderDetails();
    }, [orderId, cartStore, navigate]);

    const handleGoHome = () => {
        navigate(ROUTES.home.href);
    };

    const handleViewOrders = () => {
        // TODO: Navigate to orders page when implemented
        navigate(ROUTES.purchaseHistory.href);
    };

    if (isLoading) {
        return (
            <div className='container mx-auto px-4 py-6 md:py-8'>
                <div className='flex items-center justify-center min-h-[60vh]'>
                    <div className='text-center space-y-2'>
                        <Loader className='size-10 text-primary animate-spin mx-auto' />
                        <p className='animate-pulse'>支払い情報を確認中...</p>
                    </div>
                </div>
            </div>
        );
    }

    const isSuccess = paymentStatus === PaymentStatus.SUCCESS;

    return (
        <div className='container mx-auto px-4 py-6 md:py-8'>
            <div className='max-w-xl mx-auto'>
                <div className='bg-white rounded-lg text-center space-y-6'>
                    {/* Header with conditional title and icon */}
                    {isSuccess ?
                        (
                            <>
                                <Heading variant='h2' className='text-center'>お支払い完了</Heading>
                                <div className='w-20 h-20 bg-green-200 rounded-full flex items-center justify-center mx-auto mb-4'>
                                    <CheckCircle2 className='size-10 text-green-600' />
                                </div>
                            </>
                        ) :
                        (
                            <>
                                <Heading variant='h2' className='text-center text-danger'>支払いに失敗しました</Heading>
                                <div className='w-20 h-20 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4'>
                                    <XCircle className='size-10 text-danger' />
                                </div>
                            </>
                        )}

                    {/* Order Information - Same for both success and failure */}
                    <div className='bg-[#F1F1F1] rounded-lg p-4 sm:p-6 space-y-4'>
                        <div className='flex justify-between items-center py-2'>
                            <span>注文ID</span>
                            <span className='font-bold text-lg'>{orderDetails?.orderCode || orderId}</span>
                        </div>
                        <div className='flex justify-between items-center py-2'>
                            <span>お支払い方法</span>
                            <span className='font-bold'>{orderDetails?.paymentMethod}</span>
                        </div>
                        {orderDetails && (
                            <div className='bg-white rounded-lg p-3'>
                                <div className='flex justify-between gap-2 items-center py-2'>
                                    <span className='shrink-0'>税別支払金額</span>
                                    <span className='font-bold'>
                                        {formatPrice(orderDetails.subtotal)}
                                    </span>
                                </div>
                                <div className='flex justify-between gap-2 items-center py-2'>
                                    <span className='shrink-0'>消費税（10%）</span>
                                    <span className='font-bold'>
                                        {formatPrice(orderDetails.tax)}
                                    </span>
                                </div>
                                <div className='flex justify-between gap-2 items-center py-2'>
                                    <span className='shrink-0'>お支払い金額</span>
                                    <span className='font-bold text-xl'>
                                        {formatPrice(orderDetails.total)}
                                    </span>
                                </div>
                            </div>
                        )}
                    </div>

                    {/* Action Buttons */}
                    <div className='flex flex-col sm:flex-row gap-4 justify-center'>
                        <Button
                            onClick={handleGoHome}
                            variant='default'
                            className='min-w-40 bg-[#676767] min-h-12 flex-1 md:min-h-14 text-base'
                            size='lg'
                        >
                            トップページへ
                        </Button>
                        <Button
                            onClick={handleViewOrders}
                            className='min-w-40 min-h-12 flex-1 md:min-h-14 text-base'
                            size='lg'
                        >
                            購入履歴へ
                        </Button>
                    </div>
                </div>
            </div>
        </div>
    );
});
